import { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import {
  fetchFolio,
  fetchFulfilmentAgentMe,
  syncFolio,
  updateFolioItem,
  addFolioItem,
  deleteFolioItems,
  searchProducts,
  isAdminUser,
} from "@/lib/api";
import InventoryTab from "@/components/InventoryTab";
import FulfillmentFeesTab from "@/components/FulfillmentFeesTab";
import ShipStationTab from "@/components/ShipStationTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Boxes,
  Loader2,
  Plus,
  Minus,
  Trash2,
  Search,
  RefreshCcw,
  PackagePlus,
  DownloadCloud,
  AlertTriangle,
  X,
} from "lucide-react";
import { toast } from "sonner";

const money = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const LOW_STOCK = 3;

export default function Folio() {
  const { user } = useAuth();
  const uid = user?.user_record_id;
  const isAdmin = isAdminUser(user);
  const [tab, setTab] = useState(() => (isAdminUser(user) ? "shipstation" : "folio"));
  const [agentUid, setAgentUid] = useState(null);
  useEffect(() => {
    if (!uid || isAdmin) return;
    fetchFulfilmentAgentMe(uid).then((r) => setAgentUid(r.is_agent ? r.uid : null)).catch(() => setAgentUid(null));
  }, [uid, isAdmin]);
  const isAgent = !isAdmin && !!agentUid;

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [synced, setSynced] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [query, setQuery] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const data = await fetchFolio(uid);
      setItems(data.items || []);
      setSynced(!!data.synced);
    } catch (e) {
      toast.error("Could not load your folio");
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => (i.name || "").toLowerCase().includes(q));
  }, [items, query]);

  const totalUnits = useMemo(
    () => items.reduce((s, i) => s + (Number(i.inventory) || 0), 0),
    [items]
  );

  async function runSync() {
    if (!uid) return;
    setSyncing(true);
    try {
      const res = await syncFolio(uid);
      toast.success(
        res.added
          ? `Imported ${res.added} product${res.added === 1 ? "" : "s"} from your last 6 months`
          : "No purchases found in the last 6 months"
      );
      await load();
    } catch (e) {
      toast.error("Sync failed. Please try again.");
    } finally {
      setSyncing(false);
    }
  }

  async function setInventory(item, next) {
    const value = Math.max(0, Math.round(Number(next) || 0));
    const prev = item.inventory;
    setItems((list) =>
      list.map((i) => (i.id === item.id ? { ...i, inventory: value } : i))
    );
    try {
      await updateFolioItem({ userRecordId: uid, folioRecordId: item.id, inventory: value });
    } catch (e) {
      toast.error("Could not save count");
      setItems((list) =>
        list.map((i) => (i.id === item.id ? { ...i, inventory: prev } : i))
      );
    }
  }

  function toggleSelect(id) {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  async function removeSelected() {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setSaving(true);
    try {
      await deleteFolioItems({ userRecordId: uid, folioRecordIds: ids });
      toast.success(`Removed ${ids.length} item${ids.length === 1 ? "" : "s"}`);
      setSelected(new Set());
      setSelectMode(false);
      await load();
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setSaving(false);
    }
  }

  async function removeOne(item) {
    setItems((list) => list.filter((i) => i.id !== item.id));
    try {
      await deleteFolioItems({ userRecordId: uid, folioRecordIds: [item.id] });
    } catch (e) {
      toast.error("Delete failed");
      await load();
    }
  }

  return (
    <div className="px-4 py-5 space-y-5" data-testid="folio-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-violet-600 text-white">
              <Boxes size={20} />
            </span>
            Folio
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Your personal product inventory ·{" "}
            <span className="font-semibold text-zinc-700">{items.length}</span> item
            {items.length === 1 ? "" : "s"} ·{" "}
            <span className="font-semibold text-zinc-700">{totalUnits}</span> unit
            {totalUnits === 1 ? "" : "s"}
          </p>
        </div>
        <button
          onClick={load}
          className="shrink-0 p-2 rounded-lg text-zinc-500 hover:bg-zinc-100"
          title="Refresh"
          data-testid="folio-refresh"
        >
          <RefreshCcw size={18} />
        </button>
      </div>

      {(isAdmin || isAgent) && (
        <div className="inline-flex rounded-xl bg-zinc-100 p-1" data-testid="folio-tabs">
          {(isAdmin ? [["shipstation", "ShipStation"], ["folio", "My Folio"], ["inventory", "Inventory"], ["fees", "Fulfillment Fees"]] : [["folio", "My Folio"], ["inventory", "Inventory"], ["fees", "My Fulfillment Fees"]]).map(([v, l]) => (
            <button key={v} type="button" onClick={() => setTab(v)} className={`px-4 h-9 rounded-lg text-sm font-medium transition-colors ${tab === v ? "bg-white shadow text-zinc-900" : "text-zinc-500 hover:text-zinc-800"}`} data-testid={`folio-tab-${v}`}>{l}</button>
          ))}
        </div>
      )}

      {(isAdmin || isAgent) && tab === "inventory" ? (
        <InventoryTab callerUserRecordId={uid} readOnly={!isAdmin} />
      ) : (isAdmin || isAgent) && tab === "fees" ? (
        <FulfillmentFeesTab callerUserRecordId={uid} agentUid={isAdmin ? null : agentUid} />
      ) : isAdmin && tab === "shipstation" ? (
        <ShipStationTab callerUserRecordId={uid} />
      ) : (
      <>
      {/* One-time import banner */}
      {!synced && (
        <div
          className="rounded-xl border border-violet-200 bg-violet-50 p-4 flex items-start gap-3"
          data-testid="folio-sync-banner"
        >
          <DownloadCloud className="text-violet-600 shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="text-sm font-semibold text-violet-900">
              Import your recent purchases
            </p>
            <p className="text-xs text-violet-700 mt-0.5">
              We can seed your Folio from everything you bought in the last 6 months.
              You can adjust the counts anytime after.
            </p>
            <Button
              onClick={runSync}
              disabled={syncing}
              className="mt-3 bg-violet-600 hover:bg-violet-700"
              data-testid="folio-sync-btn"
            >
              {syncing ? (
                <Loader2 className="animate-spin mr-2" size={15} />
              ) : (
                <DownloadCloud className="mr-2" size={15} />
              )}
              {syncing ? "Importing…" : "Import last 6 months"}
            </Button>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
            size={16}
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search my inventory"
            className="pl-9"
            data-testid="folio-search"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => setAddOpen(true)}
          data-testid="folio-add-open"
        >
          <PackagePlus size={16} className="mr-1.5" />
          Add
        </Button>
        <Button
          variant={selectMode ? "default" : "outline"}
          onClick={() => {
            setSelectMode((v) => !v);
            setSelected(new Set());
          }}
          data-testid="folio-select-toggle"
        >
          {selectMode ? "Done" : "Select"}
        </Button>
      </div>

      {/* Bulk delete bar */}
      {selectMode && selected.size > 0 && (
        <div className="flex items-center justify-between rounded-lg bg-zinc-900 text-white px-4 py-2.5">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <Button
            size="sm"
            variant="destructive"
            onClick={removeSelected}
            disabled={saving}
            data-testid="folio-bulk-delete"
          >
            {saving ? (
              <Loader2 className="animate-spin mr-1.5" size={14} />
            ) : (
              <Trash2 size={14} className="mr-1.5" />
            )}
            Remove
          </Button>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-violet-600" size={28} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-zinc-400" data-testid="folio-empty">
          <Boxes size={40} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">
            {items.length === 0
              ? "Your inventory is empty. Import your purchases or add products."
              : "No items match your search."}
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="folio-list">
          {filtered.map((item) => {
            const low = (Number(item.inventory) || 0) <= LOW_STOCK;
            const isSel = selected.has(item.id);
            return (
              <li
                key={item.id}
                className={`flex items-center gap-3 rounded-xl border bg-white p-3 transition-colors ${
                  isSel ? "border-violet-400 ring-1 ring-violet-300" : "border-zinc-200"
                }`}
                data-testid="folio-row"
              >
                {selectMode && (
                  <input
                    type="checkbox"
                    checked={isSel}
                    onChange={() => toggleSelect(item.id)}
                    className="h-5 w-5 rounded border-2 border-zinc-300 accent-violet-600"
                    data-testid="folio-row-checkbox"
                  />
                )}
                <div className="w-12 h-12 rounded-lg bg-zinc-100 overflow-hidden shrink-0">
                  {item.image_url ? (
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-full h-full object-cover"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  ) : null}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-zinc-900 truncate">
                    {item.name}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-zinc-500">{money(item.price)}</span>
                    {low && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600">
                        <AlertTriangle size={11} /> Low
                      </span>
                    )}
                  </div>
                </div>

                {/* Stepper */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => setInventory(item, item.inventory - 1)}
                    className="w-8 h-8 rounded-lg border border-zinc-200 flex items-center justify-center text-zinc-600 hover:bg-zinc-50 disabled:opacity-40"
                    disabled={item.inventory <= 0}
                    data-testid="folio-dec"
                  >
                    <Minus size={15} />
                  </button>
                  <input
                    type="number"
                    value={item.inventory}
                    min={0}
                    onChange={(e) =>
                      setItems((list) =>
                        list.map((i) =>
                          i.id === item.id
                            ? { ...i, inventory: e.target.value }
                            : i
                        )
                      )
                    }
                    onBlur={(e) => setInventory(item, e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                    className="w-12 h-8 text-center text-sm font-semibold rounded-lg border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-300"
                    data-testid="folio-qty-input"
                  />
                  <button
                    onClick={() => setInventory(item, Number(item.inventory) + 1)}
                    className="w-8 h-8 rounded-lg border border-zinc-200 flex items-center justify-center text-zinc-600 hover:bg-zinc-50"
                    data-testid="folio-inc"
                  >
                    <Plus size={15} />
                  </button>
                  {!selectMode && (
                    <button
                      onClick={() => removeOne(item)}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-rose-400 hover:bg-rose-50 hover:text-rose-600"
                      data-testid="folio-row-delete"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <AddProductDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        userRecordId={uid}
        existingPostIds={new Set(items.map((i) => String(i.post_id)))}
        onAdded={load}
      />
      </>
      )}
    </div>
  );
}

function AddProductDialog({ open, onOpenChange, userRecordId, existingPostIds, onAdded }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingId, setAddingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await searchProducts(q, 30);
        if (active) setResults(data.products || []);
      } catch {
        if (active) setResults([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [q, open]);

  async function add(p) {
    setAddingId(p.id);
    try {
      await addFolioItem({ userRecordId, productRecordId: p.id, qty: 1 });
      toast.success(`Added ${p.name} to your folio`);
      await onAdded();
    } catch {
      toast.error("Could not add product");
    } finally {
      setAddingId(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="folio-add-dialog">
        <DialogHeader>
          <DialogTitle>Add a product</DialogTitle>
          <DialogDescription className="sr-only">
            Search the catalogue and add a product to your Folio inventory.
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
            size={16}
          />
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search catalogue"
            className="pl-9"
            data-testid="folio-add-search"
          />
        </div>
        <div className="max-h-80 overflow-y-auto space-y-1.5 -mx-1 px-1">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-violet-600" size={22} />
            </div>
          ) : results.length === 0 ? (
            <p className="text-center text-sm text-zinc-400 py-8">No products found</p>
          ) : (
            results.map((p) => {
              const already = existingPostIds.has(String(p.post_id));
              return (
                <div
                  key={p.id}
                  className="flex items-center gap-3 rounded-lg border border-zinc-200 p-2"
                  data-testid="folio-add-result"
                >
                  <div className="w-10 h-10 rounded-md bg-zinc-100 overflow-hidden shrink-0">
                    {p.image_url ? (
                      <img
                        src={p.image_url}
                        alt={p.name}
                        className="w-full h-full object-cover"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-900 truncate">{p.name}</p>
                    <p className="text-xs text-zinc-500">{money(p.price)}</p>
                  </div>
                  <Button
                    size="sm"
                    variant={already ? "outline" : "default"}
                    onClick={() => add(p)}
                    disabled={addingId === p.id}
                    className={already ? "" : "bg-violet-600 hover:bg-violet-700"}
                    data-testid="folio-add-result-btn"
                  >
                    {addingId === p.id ? (
                      <Loader2 className="animate-spin" size={14} />
                    ) : already ? (
                      "+1"
                    ) : (
                      <Plus size={14} />
                    )}
                  </Button>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
